// TypeScript wrapper and interface for centralized SEO Engine
// Mirrors scripts/seo_engine.mjs for frontend components

export const BASE_URL = 'https://cameroonlandmarket.com';
export const CURRENT_YEAR = new Date().getFullYear();

import { hasCompleteCityContent } from '../content/cityContent/completedCities';
import { ALL_CAMEROON_TOWNS } from '../data/cameroonCities';

export const ALL_CAMEROON_TOWN_SLUGS = new Set(
  (ALL_CAMEROON_TOWNS || []).map((t) => (t.slug || '').toLowerCase().trim()).filter(Boolean)
);

export interface SeoPageInput {
  pageType?:
    | 'home'
    | 'national_land'
    | 'city_land'
    | 'city_properties'
    | 'city_rent'
    | 'quarter_land'
    | 'guide_buying'
    | 'guide_titre_foncier'
    | 'guide_scams'
    | 'custom';
  lang?: 'en' | 'fr';
  city?: string;
  citySlug?: string;
  quarter?: string;
  quarterSlug?: string;
  priceRange?: string;
  minPrice?: string;
  quarters?: Array<{ name: string } | string>;
  listingCount?: number;
  year?: number;
  customTitle?: string;
  customDesc?: string;
  canonicalPath?: string;
  path?: string;
  hreflangEn?: string;
  hreflangFr?: string;
  hreflangXDefault?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: 'website' | 'article' | 'product';
  coords?: [number, number];
  cityMeta?: { coords?: [number, number]; city?: string; region?: string };
  noIndex?: boolean;
}

export interface SeoMetadataOutput {
  title: string;
  description: string;
  canonicalUrl: string;
  hreflangs: {
    en: string;
    fr: string;
    enCm?: string;
    frCm?: string;
    xDefault: string;
  };
  og: {
    title: string;
    description: string;
    url: string;
    type: 'website' | 'article' | 'product';
    image: string;
    imageAlt: string;
    locale: string;
    localeAlternate: string;
  };
  twitter: {
    title: string;
    description: string;
    image: string;
    card: string;
  };
  geo: {
    position: string;
    icbm: string;
    placename: string;
    region: string;
  } | null;
  noIndex: boolean;
}

export function safeTruncate(text: string, maxLen: number): string {
  if (!text || text.length <= maxLen) return text || '';
  const sub = text.slice(0, maxLen);
  const lastSpace = sub.lastIndexOf(' ');
  let result = sub;
  if (lastSpace > maxLen * 0.65) {
    result = sub.slice(0, lastSpace);
  }
  return result.replace(/[\s|\-:–,]+$/, '').trim();
}

export function fitDescription(text: string, lang = 'en'): string {
  if (!text) return '';
  let clean = text.replace(/\s+/g, ' ').trim();
  const isFr = lang === 'fr';

  if (clean.length > 160) {
    clean = safeTruncate(clean, 160);
    if (!/[.!?]$/.test(clean)) {
      if (clean.length <= 159) clean += '.';
    }
  }

  if (clean.length < 140) {
    const buildingBlocks = isFr
      ? [
          " Vérifiez le titre avant d'acheter.",
          ' Contactez directement les vendeurs.',
          ' Contactez les propriétaires.',
          ' Consultez les annonces en ligne.',
          ' Déposez une annonce gratuite.',
          ' Consultez nos annonces.',
          ' Petites annonces gratuites.',
          ' Annonces gratuites.',
          ' Guide foncier au Cameroun.',
          ' Portail immobilier.',
          ' Au Cameroun.',
        ]
      : [
          ' Check the title before you buy.',
          ' Contact verified owners directly.',
          ' Contact owners directly.',
          ' Browse latest listings online.',
          ' Post a free property ad.',
          ' Browse our latest plots.',
          ' Free property classifieds.',
          ' Free classifieds.',
          ' Land guide in Cameroon.',
          ' Real estate portal.',
          ' In Cameroon.',
        ];

    // Try single-block match first that lands squarely in [140, 160]
    for (const block of buildingBlocks) {
      const len = clean.length + block.length;
      if (len >= 140 && len <= 160) {
        clean += block;
        break;
      }
    }

    // If still < 140, greedily add blocks that fit <= 160
    if (clean.length < 140) {
      for (const block of buildingBlocks) {
        if (clean.length + block.length <= 160) {
          clean += block;
          if (clean.length >= 140) break;
        }
      }
    }

    // If still < 140, try suffixes
    if (clean.length < 140) {
      const suffixes = isFr
        ? [
            ' Trouvez votre bien idéal avec Cameroon Land Market.',
            ' Consultez toutes les offres disponibles au Cameroun.',
            ' Votre portail d\'annonces immobilières au Cameroun.',
            ' Guide et annonces foncières au Cameroun.',
            ' Petites annonces gratuites au Cameroun.',
          ]
        : [
            ' Find your ideal property on Cameroon Land Market.',
            ' Browse all available land and properties in Cameroon.',
            ' Your trusted real estate marketplace in Cameroon.',
            ' Free property and land classifieds in Cameroon.',
            ' Free classified ads in Cameroon.',
          ];

      for (const s of suffixes) {
        const len = clean.length + s.length;
        if (len >= 140 && len <= 160) {
          clean += s;
          break;
        }
      }
    }
  }

  if (clean.length < 140) {
    const filler = isFr
      ? ' Annonces immobilières et parcelles titrées au Cameroun.'
      : ' Real estate listings and titled plots in Cameroon.';
    const combined = clean + filler;
    if (combined.length <= 160 && combined.length >= 140) {
      clean = combined;
    } else if (combined.length > 160) {
      clean = safeTruncate(combined, 160);
      if (!/[.!?]$/.test(clean) && clean.length <= 159) clean += '.';
    }
  }

  return clean;
}

export function getPageOgImage(options: { citySlug?: string; categoryId?: string; guideId?: string; lang?: 'en' | 'fr' } = {}): {
  url: string;
  alt: string;
} {
  const { citySlug, categoryId, guideId, lang = 'en' } = options;
  const isFr = lang === 'fr';
  const slug = (citySlug || '').toLowerCase();
  const cat = (categoryId || '').toLowerCase();
  const guide = (guideId || '').toLowerCase();

  if (guide.includes('anti-scam') || guide.includes('scam')) {
    return {
      url: `${BASE_URL}/hero-video-poster.jpg`,
      alt: isFr
        ? 'Prévention des fraudes foncières et checklist de vérification au Cameroun'
        : 'Cameroon land fraud prevention and due diligence checklist',
    };
  }
  if (guide.includes('titre-foncier') || guide.includes('cadastre')) {
    return {
      url: `${BASE_URL}/images/categories/land.jpg`,
      alt: isFr
        ? 'Guide pas à pas de vérification du Titre Foncier au MINDCAF'
        : 'Step-by-step Titre Foncier cadastral verification guide at MINDCAF',
    };
  }
  if (guide.includes('guide') || guide.includes('buying')) {
    return {
      url: `${BASE_URL}/images/categories/land.jpg`,
      alt: isFr
        ? 'Guide d’achat sécurisé de terrains titrés au Cameroun'
        : 'Due diligence guide for buying titled land in Cameroon',
    };
  }

  if (slug === 'douala') {
    return {
      url: `${BASE_URL}/douala-wouri-skyline.jpg`,
      alt: isFr
        ? 'Panorama de Douala et parcelles immobilières titrées au Cameroun'
        : 'Douala skyline and titled real estate plots, Cameroon',
    };
  }
  if (slug === 'yaounde') {
    return {
      url: `${BASE_URL}/images/locations/yaounde.jpg`,
      alt: isFr
        ? 'Quartiers résidentiels et administratifs de Yaoundé, Cameroun'
        : 'Yaoundé administrative and residential property districts, Cameroon',
    };
  }
  if (slug === 'kribi') {
    return {
      url: `${BASE_URL}/images/locations/kribi.jpg`,
      alt: isFr
        ? 'Terrains balnéaires et parcelles côtières à Kribi, Cameroun'
        : 'Kribi coastal beach land and beachfront parcels, Cameroon',
    };
  }
  if (slug === 'buea') {
    return {
      url: `${BASE_URL}/images/locations/buea.jpg`,
      alt: isFr
        ? 'Terrains résidentiels au pied du Mont Cameroun à Buea'
        : 'Buea Mount Cameroon residential land and property, Cameroon',
    };
  }
  if (slug === 'limbe') {
    return {
      url: `${BASE_URL}/limbe-black-sand-beach.jpg`,
      alt: isFr
        ? 'Parcelles côtières et immobilier sur la côte Atlantique à Limbe'
        : 'Limbe Atlantic coast parcels and real estate, Cameroon',
    };
  }
  if (slug === 'bamenda') {
    return {
      url: `${BASE_URL}/images/locations/bamenda.jpg`,
      alt: isFr
        ? 'Collines et parcelles titrées à Bamenda, Cameroun'
        : 'Bamenda scenic hills and titled land parcels, Cameroon',
    };
  }

  if (cat.includes('land') || cat.includes('terrain')) {
    return {
      url: `${BASE_URL}/images/categories/land.jpg`,
      alt: isFr
        ? 'Terrains titrés et parcelles à bâtir à vendre au Cameroun'
        : 'Titled land parcels and building plots for sale in Cameroon',
    };
  }
  if (cat.includes('house') || cat.includes('maison')) {
    return {
      url: `${BASE_URL}/images/categories/houses-sale.jpg`,
      alt: isFr
        ? 'Villas modernes, duplex et maisons à vendre au Cameroun'
        : 'Modern villas, duplexes and houses for sale in Cameroon',
    };
  }
  if (cat.includes('apartment') && (cat.includes('rent') || cat.includes('louer'))) {
    return {
      url: `${BASE_URL}/images/categories/apartments-rent.jpg`,
      alt: isFr
        ? 'Appartements, studios et logements à louer au Cameroun'
        : 'Rental apartments, studios and flats in Cameroon',
    };
  }
  if (cat.includes('apartment') || cat.includes('appartement')) {
    return {
      url: `${BASE_URL}/images/categories/apartments-sale.jpg`,
      alt: isFr
        ? 'Appartements et résidences à vendre au Cameroun'
        : 'Apartments and residential flats for sale in Cameroon',
    };
  }
  if (cat.includes('commercial')) {
    return {
      url: `${BASE_URL}/images/categories/commercial-sale.jpg`,
      alt: isFr
        ? 'Immobilier commercial, bureaux et boutiques au Cameroun'
        : 'Commercial property, offices and shops in Cameroon',
    };
  }
  if (cat.includes('short-stay') || cat.includes('hotel') || cat.includes('stay')) {
    return {
      url: `${BASE_URL}/images/categories/short-stays.jpg`,
      alt: isFr
        ? 'Courts séjours, appartements meublés et vacances au Cameroun'
        : 'Short-stay apartments and holiday rentals in Cameroon',
    };
  }

  return {
    url: `${BASE_URL}/mount-cameroon-fako.jpg`,
    alt: isFr
      ? 'Cameroon Land Market – Petites annonces immobilières gratuites et bilingues'
      : 'Cameroon Land Market – Free bilingual property classifieds',
  };
}

export const MIN_ACTIVE_LISTINGS_FOR_INDEX = 3;
export const MIN_UNIQUE_WORDS_FOR_INDEX = 300;

export const PROHIBITED_ROBOTS_PREFIXES = [
  '/signin',
  '/connexion',
  '/login',
  '/signup',
  '/inscription',
  '/register',
  '/forgot-password',
  '/mot-de-passe-oublie',
  '/reset-password',
  '/reinitialiser-mot-de-passe',
  '/auth-action',
  '/post-property',
  '/deposer-annonce',
  '/publier-annonce',
  '/post',
  '/account',
  '/compte',
  '/dashboard',
  '/tableau-de-bord',
  '/profile',
  '/mon-profil',
  '/my-listings',
  '/manage-listings',
  '/mes-annonces',
  '/renew',
  '/renouveler',
  '/clm-master-vault-9820',
  '/admin',
  '/api',
];

/**
 * Universal isIndexable(page) function.
 * A page is indexable ONLY if it has >= 3 active listings OR >= 300 words of unique content.
 * Otherwise outputs noindex, follow and is excluded from sitemaps.
 * Flips to index automatically when the page qualifies.
 */
export function isIndexable(page: any): boolean {
  if (!page) return false;

  // 1. Explicit noIndex override
  if (page.noIndex === true || page.noindex === true) return false;

  const pathToCheck = typeof page === 'string'
    ? page
    : (page.canonicalPath || page.en || page.loc || page.path || '');
  const cleanPath = pathToCheck.replace(/^https?:\/\/[^/]+/i, '').trim();

  // Strip language prefix for prefix evaluation
  const cleanPathNoLang = cleanPath.replace(/^\/(en|fr)(\/|$)/i, '/');

  // 2. Prohibited private and functional prefixes
  for (const prefix of PROHIBITED_ROBOTS_PREFIXES) {
    if (
      cleanPath === prefix ||
      cleanPath.startsWith(prefix + '/') ||
      cleanPathNoLang === prefix ||
      cleanPathNoLang.startsWith(prefix + '/')
    ) {
      return false;
    }
  }

  // 3. Disallow internal search and filter parameter URLs
  if (cleanPath.includes('?') || cleanPath.includes('&')) {
    return false;
  }

  // 4. Listing Document Validation (if listing document passed)
  if (
    page.status !== undefined ||
    page.isPublic !== undefined ||
    page.expiresAt ||
    page.expires_at ||
    page.isDeleted !== undefined ||
    page.deleted_at
  ) {
    if (page.deleted_at || page.deletedAt || page.isDeleted === true) return false;
    const status = (page.status || '').toLowerCase().trim();
    if (['draft', 'pending', 'pending_review', 'rejected', 'archived', 'deleted', 'sold', 'rented', 'expired'].includes(status)) {
      return false;
    }
    const rawExpiresAt = page.expires_at || page.expiresAt;
    if (rawExpiresAt) {
      const expDate = typeof rawExpiresAt?.toDate === 'function' ? rawExpiresAt.toDate() : new Date(rawExpiresAt);
      if (!isNaN(expDate.getTime()) && expDate.getTime() <= Date.now()) return false;
    }
    return true; // Active individual property listing is indexable
  }

  // 5. Evergreen Pillar Pages (Home, Legal, Resources Hub, About, Contact)
  const ALWAYS_INDEXABLE_EXACT = [
    '/', '/en', '/fr',
    '/en/about', '/fr/a-propos',
    '/en/contact', '/fr/contact',
    '/en/map', '/fr/carte',
    '/en/resources', '/fr/ressources',
    '/en/verify-property', '/fr/verifier-propriete',
    '/en/verify-titre-foncier', '/fr/verifier-titre-foncier',
    '/en/legal/terms', '/fr/mentions-legales/conditions',
    '/en/legal/privacy', '/fr/mentions-legales/confidentialite',
    '/en/terms', '/fr/conditions-utilisation',
    '/en/privacy', '/fr/confidentialite',
    '/en/faq', '/fr/faq',
    '/en/sitemap', '/fr/plan-du-site',
    '/en/professionals', '/fr/professionnels',
  ];
  if (
    ALWAYS_INDEXABLE_EXACT.includes(cleanPath) ||
    cleanPath.startsWith('/en/professionals/') ||
    cleanPath.startsWith('/fr/professionnels/') ||
    cleanPath.startsWith('/fr/professionals/') ||
    cleanPath.startsWith('/professionals/')
  ) {
    return true;
  }

  // 6. Editorial Guides and Resource Articles (> 500 words of rich content)
  if (
    cleanPath.includes('/resources/') ||
    cleanPath.includes('/ressources/') ||
    cleanPath.includes('guide') ||
    cleanPath.includes('titre-foncier')
  ) {
    return true;
  }

  // 7. Core City Guide Hub Pages (/en/cities, /fr/villes, /en/cities/{city}, /fr/villes/{city})
  const isCityDirectory = cleanPath === '/en/cities' || cleanPath === '/fr/villes';
  const isCityGuide = (cleanPath.startsWith('/en/cities/') || cleanPath.startsWith('/fr/villes/')) && cleanPath.split('/').length === 4;
  if (isCityDirectory) {
    return true;
  }
  if (isCityGuide) {
    const citySlug = (page.citySlug || cleanPath.split('/')[3] || '').toLowerCase().trim();
    if (hasCompleteCityContent(citySlug)) {
      return true;
    }
    const activeCount = typeof page.listingCount === 'number'
      ? page.listingCount
      : (typeof page.activeListingsCount === 'number' ? page.activeListingsCount : 0);
    return activeCount >= 1;
  }

  // 7b. Region Pages (/en/regions, /fr/regions, /en/regions/{slug}, /fr/regions/{slug})
  const isRegionHub = cleanPath === '/en/regions' || cleanPath === '/fr/regions' || cleanPath.startsWith('/en/regions/') || cleanPath.startsWith('/fr/regions/');
  if (isRegionHub) {
    return true;
  }

  // 8. National Category Hub Pages (/en/land-for-sale, /fr/terrains-a-vendre, etc.)
  const isNationalCategory = [
    '/en/land-for-sale', '/fr/terrains-a-vendre',
    '/en/properties-for-sale', '/fr/biens-a-vendre',
    '/en/houses-for-sale', '/fr/maisons-a-vendre',
    '/en/apartments-for-sale', '/fr/appartements-a-vendre',
    '/en/apartments-for-rent', '/fr/appartements-a-louer',
    '/en/properties-for-rent', '/fr/biens-a-louer',
    '/en/commercial-for-sale', '/fr/commercial-a-vendre',
    '/en/commercial-property-for-sale', '/fr/immobilier-commercial-a-vendre',
    '/en/shops-and-offices-for-sale', '/fr/boutiques-et-bureaux-a-vendre',
    '/en/houses-for-rent', '/fr/maisons-a-louer',
    '/en/studios-for-rent', '/fr/studios-a-louer',
    '/en/rooms-for-rent', '/en/single-rooms-for-rent', '/fr/chambres-a-louer',
    '/en/land-for-rent', '/fr/terrains-a-louer',
    '/en/commercial-for-rent', '/fr/commercial-a-louer', '/fr/locaux-commerciaux-a-louer',
    '/en/shops-and-offices-for-rent', '/fr/boutiques-et-bureaux-a-louer',
    '/en/short-stays', '/en/short-stay-apartments', '/fr/sejours-courts', '/fr/courts-sejours',
    '/en/hotels', '/fr/hotels',
    '/en/guesthouses', '/en/guesthouses-lodges', '/fr/maisons-dhotes', '/fr/guesthouses',
    '/en/motels', '/fr/motels',
    '/en/resorts', '/en/resorts-holiday-homes', '/fr/resorts', '/fr/complexes-hoteliers',
    '/en/serviced-apartments', '/fr/appartements-meubles', '/fr/residences', '/fr/residences-meublees', '/fr/serviced-apartments',
    '/fr/proprietes-a-vendre', '/fr/proprietes-a-louer',
  ].includes(cleanPath);

  if (isNationalCategory) {
    const activeCount = typeof page.listingCount === 'number' ? page.listingCount : (page.activeListingsCount || 0);
    const wordCount = typeof page.wordCount === 'number' ? page.wordCount : (page.uniqueWordCount || 400);
    return activeCount >= MIN_ACTIVE_LISTINGS_FOR_INDEX || wordCount >= MIN_UNIQUE_WORDS_FOR_INDEX;
  }

  // 9a. Dedicated Category + Region & Category + Region + City Landing Pages
  if (page.categoryRegionMeta || page.isCategoryRegion || page.categoryRegionCityMeta || page.isCategoryRegionCity) {
    return true;
  }

  // 9b. City-Category and Region-Category Pages
  const pathParts = cleanPath.split('/');
  const CAMEROON_REGION_SLUGS = new Set([
    'centre', 'littoral', 'south-west', 'sud-ouest', 'south', 'sud',
    'west', 'ouest', 'north-west', 'nord-ouest', 'adamawa', 'adamaoua',
    'north', 'nord', 'far-north', 'extreme-nord', 'east', 'est'
  ]);

  // For 2-level city-category hubs: /en/category/city (pathParts.length === 4)
  if (pathParts.length === 4 && (pathParts[1] === 'en' || pathParts[1] === 'fr')) {
    const segment2 = (pathParts[3] || '').toLowerCase().trim();
    if (
      CAMEROON_REGION_SLUGS.has(segment2) ||
      ALL_CAMEROON_TOWN_SLUGS.has(segment2) ||
      hasCompleteCityContent(segment2)
    ) {
      const activeCount = typeof page.listingCount === 'number'
        ? page.listingCount
        : (typeof page.activeListingsCount === 'number' ? page.activeListingsCount : 0);
      if (activeCount > 0 || hasCompleteCityContent(segment2)) {
        return true;
      }
    }
  }

  // 9c. Deep Quarter Pages (/en/category/city/quarter, length >= 5)
  // Strictly enforce: MUST have active listings >= 1 or unique editorial content >= 300 words!
  const activeCount = typeof page.listingCount === 'number'
    ? page.listingCount
    : (typeof page.activeListingsCount === 'number' ? page.activeListingsCount : 0);

  const wordCount = typeof page.wordCount === 'number'
    ? page.wordCount
    : (typeof page.uniqueWordCount === 'number' ? page.uniqueWordCount : 0);

  return activeCount >= 1 || wordCount >= MIN_UNIQUE_WORDS_FOR_INDEX;
}

/**
 * Extracts a compact numeric price from price strings like "35,000 - 180,000 XAF/m²" -> "35,000"
 */
export function extractMinPrice(priceStr?: string | null): string | null {
  if (!priceStr || typeof priceStr !== 'string') return null;
  const match = priceStr.match(/(\d[\d,\s]*)/);
  if (!match) return null;
  const numStr = match[1].trim().replace(/\s/g, ',');
  return numStr || null;
}

export function generatePageSeoMetadata(input: SeoPageInput = {}): SeoMetadataOutput {
  const lang = input.lang === 'fr' ? 'fr' : 'en';
  const isFr = lang === 'fr';
  const pageType = input.pageType || 'custom';
  const city = input.city || '';
  const quarter = input.quarter || '';
  const minPrice = input.minPrice || extractMinPrice(input.priceRange);
  const quartersList = Array.isArray(input.quarters)
    ? input.quarters.slice(0, 3).map((q) => (typeof q === 'string' ? q : q.name))
    : [];
  const listingCount = typeof input.listingCount === 'number' && input.listingCount > 0 ? input.listingCount : 0;
  const year = input.year || CURRENT_YEAR;

  let rawTitle = '';
  let rawDesc = '';

  switch (pageType) {
    case 'home':
      rawTitle = isFr
        ? 'Terrains & Biens Immobiliers au Cameroun – Annonces'
        : 'Land & Property for Sale in Cameroon – Free Ads';
      rawDesc = isFr
        ? 'Achetez ou vendez terrains, maisons et appartements au Cameroun. Annonces à Douala, Yaoundé, Kribi et Buea. Déposez votre annonce gratuite en 2 min.'
        : 'Buy or sell land, houses and apartments in Cameroon. Browse plots in Douala, Yaoundé, Kribi and Buea. Post your property ad free in 2 minutes.';
      break;

    case 'national_land':
      rawTitle = isFr
        ? 'Terrains à Vendre au Cameroun – Parcelles Titrées'
        : 'Land for Sale in Cameroon – Titled Plots & Prices';
      rawDesc = isFr
        ? 'Trouvez un terrain à vendre au Cameroun. Parcelles titrées, prix au m² et terrains constructibles dans les 10 régions. Contactez les propriétaires.'
        : 'Find titled land for sale across Cameroon. Explore verified cadastral plots, prices per m² and building land in all 10 regions. Contact sellers now.';
      break;

    case 'city_land':
      if (minPrice) {
        rawTitle = isFr
          ? `Terrain à Vendre à ${city} – Dès ${minPrice} FCFA/m²`
          : `Land for Sale in ${city} – Plots from ${minPrice} XAF/m²`;
      } else {
        rawTitle = isFr
          ? `Terrain à Vendre à ${city} – Parcelles Titrées`
          : `Land for Sale in ${city} – Titled Plots & Prices`;
      }

      if (quartersList.length >= 2) {
        const qStr = quartersList.join(', ');
        rawDesc = isFr
          ? `Parcelles résidentielles et commerciales à ${city}, dont ${qStr}. Guide des prix, vérifiez le titre avant d'acheter, contactez les vendeurs.`
          : `Residential and commercial plots in ${city}, including ${qStr}. Cadastral price guide, check the title before you buy, contact sellers now.`;
      } else {
        rawDesc = isFr
          ? `Parcelles résidentielles et commerciales à ${city}. Données cadastrales, prix au m², vérifiez le titre avant d'acheter et contactez vendeurs en direct.`
          : `Residential and commercial plots in ${city}. Cadastral zoning, average prices per m², check title before buying and contact sellers. Post free ad.`;
      }
      break;

    case 'city_properties':
      rawTitle = isFr
        ? `Immobilier à Vendre à ${city} – Maisons & Terrains`
        : `Properties for Sale in ${city} – Houses & Land`;
      rawDesc = isFr
        ? `Consultez maisons, terrains titrés, villas et locaux commerciaux à vendre à ${city}. Repères de prix, vérifiez le titre et contactez les vendeurs directs.`
        : `Browse houses, titled plots, villas and commercial real estate for sale in ${city}. Cadastral price guide, verify the title, contact direct sellers now.`;
      break;

    case 'city_rent':
      rawTitle = isFr
        ? `Appartement à Louer à ${city} – Studios & Logements`
        : `Apartments for Rent in ${city} – Flats & Studios`;
      rawDesc = isFr
        ? `Appartements à louer, studios meublés et maisons de standing à ${city}. Comparez les loyers, découvrez les quartiers sûrs et contactez les propriétaires.`
        : `Find apartments, furnished studios and rental houses across ${city}. Compare monthly rental prices, discover safe residential quarters, contact owners.`;
      break;

    case 'quarter_land':
      rawTitle = isFr
        ? `Terrain à Vendre à ${quarter}, ${city} – Prix`
        : `Land for Sale in ${quarter}, ${city} – Prices & Plots`;
      rawDesc = isFr
        ? `Terrain à vendre à ${quarter}, ${city}. Prix au m², bornage cadastral, vérifiez le titre foncier avant d'acheter. Contactez les propriétaires en direct.`
        : `Land for sale in ${quarter}, ${city}. Cadastral price benchmarks, titled parcel boundaries, check the title before you buy. Contact private owners today.`;
      break;

    case 'guide_buying':
      rawTitle = isFr
        ? `Acheter un Terrain au Cameroun : Guide (${year})`
        : `How to Buy Land in Cameroon: Step-by-Step Guide (${year})`;
      rawDesc = isFr
        ? `Acheter un terrain en sécurité au Cameroun (${year}). Démarches pas à pas : vérification du Titre Foncier, bornage, acte notarié et pièges à éviter.`
        : `How to buy land safely in Cameroon (${year}). Step-by-step Titre Foncier verification, cadastral survey beacons, notary deed signing and scam avoidance.`;
      break;

    case 'guide_titre_foncier':
      rawTitle = isFr
        ? 'Vérifier un Titre Foncier au Cameroun – Cadastre'
        : 'Verify a Titre Foncier in Cameroon – Step-by-Step';
      rawDesc = isFr
        ? `Comment vérifier un Titre Foncier au Cameroun pas à pas : réquisition cadastrale, Certificat de Propriété MINDCAF et contrôles notariés indispensables.`
        : `Learn how to verify a Titre Foncier in Cameroon step by step. Cadastral search, Certificate of Ownership at MINDCAF and legal due diligence before buying.`;
      break;

    case 'guide_scams':
      rawTitle = isFr
        ? 'Arnaques Foncières au Cameroun : Pièges à Éviter'
        : 'Cameroon Land Scams: Red Flags to Check Before Paying';
      rawDesc = isFr
        ? `Arnaques foncières et faux titres au Cameroun : signaux d'alerte, vérification du bornage, doubles ventes et précautions avant tout versement.`
        : `Cameroon land scams and red flags to check before paying. Warning signs of forged titles, double sales, fake surveyors and safe payment precautions.`;
      break;

    default:
      rawTitle = input.customTitle || (isFr ? 'Immobilier & Terrains au Cameroun' : 'Property & Land for Sale in Cameroon');
      rawDesc =
        input.customDesc ||
        (isFr
          ? 'Trouvez des terrains titrés, maisons et appartements au Cameroun. Annonces directes, vérification de titre foncier et publication gratuite.'
          : 'Find titled land, houses and apartments across Cameroon. Direct owner listings, title due diligence support and free property ad posting.');
      break;
  }

  if (listingCount > 0) {
    let countWordEn = 'listings';
    if (pageType === 'national_land' || pageType === 'city_land' || pageType === 'quarter_land') {
      countWordEn = listingCount === 1 ? 'plot' : 'plots';
    } else if (pageType === 'city_rent') {
      countWordEn = listingCount === 1 ? 'rental' : 'rentals';
    } else {
      countWordEn = listingCount === 1 ? 'property' : 'properties';
    }
    const countSuffix = isFr
      ? (listingCount === 1 ? ' (1 annonce)' : ` (${listingCount} annonces)`)
      : (listingCount === 1 ? ` (1 ${countWordEn})` : ` (${listingCount} ${countWordEn})`);
    if (rawTitle.length + countSuffix.length <= 60) {
      rawTitle += countSuffix;
    }
  }

  const title = safeTruncate(rawTitle, 60);
  const description = fitDescription(rawDesc, lang);

  const rawPath = (input.canonicalPath || input.path || (isFr ? '/fr' : '/en')).replace(/\/+$/, '');
  const cleanPath = !rawPath || rawPath === '/'
    ? (isFr ? '/fr' : '/en')
    : rawPath.startsWith('http') || rawPath === '/fr' || rawPath.startsWith('/fr/') || rawPath === '/en' || rawPath.startsWith('/en/')
    ? rawPath
    : `${isFr ? '/fr' : '/en'}${rawPath.startsWith('/') ? rawPath : '/' + rawPath}`;
  const canonicalUrl = cleanPath.startsWith('http') ? cleanPath : `${BASE_URL}${cleanPath}`;

  let enHreflang = input.hreflangEn;
  let frHreflang = input.hreflangFr;

  if (!enHreflang || !frHreflang) {
    let baseClean = cleanPath.replace(/^\/(fr|en)(\/|$)/i, '/');
    if (!baseClean.startsWith('/')) baseClean = '/' + baseClean;
    const baseSlug = baseClean === '/' ? '' : baseClean;

    enHreflang = `${BASE_URL}/en${baseSlug}`;
    frHreflang = `${BASE_URL}/fr${baseSlug}`;
  }

  const resolvedEn = enHreflang.startsWith('http') ? enHreflang : `${BASE_URL}${enHreflang}`;
  const resolvedFr = frHreflang.startsWith('http') ? frHreflang : `${BASE_URL}${frHreflang}`;
  // Fallback x-default strictly points to the French version for Cameroon target audience
  const resolvedXDefault = resolvedFr;

  const ogImg = input.ogImage
    ? { url: input.ogImage, alt: input.ogImageAlt || title }
    : getPageOgImage({ citySlug: input.citySlug, categoryId: input.path, guideId: input.path, lang: isFr ? 'fr' : 'en' });

  let geo = null;
  const coords = input.coords || input.cityMeta?.coords;
  if (coords && Array.isArray(coords) && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    const placename = quarter && city ? `${quarter}, ${city}, Cameroon` : city ? `${city}, Cameroon` : 'Cameroon';
    geo = {
      position: `${coords[0]};${coords[1]}`,
      icbm: `${coords[0]}, ${coords[1]}`,
      placename,
      region: 'CM',
    };
  }

  return {
    title,
    description,
    canonicalUrl,
    hreflangs: {
      en: resolvedEn,
      fr: resolvedFr,
      enCm: resolvedEn,
      frCm: resolvedFr,
      xDefault: resolvedXDefault,
    },
    og: {
      title,
      description,
      url: canonicalUrl,
      type: input.ogType || 'website',
      image: ogImg.url,
      imageAlt: ogImg.alt,
      locale: isFr ? 'fr_CM' : 'en_CM',
      localeAlternate: isFr ? 'en_CM' : 'fr_CM',
    },
    twitter: {
      title,
      description,
      image: ogImg.url,
      card: 'summary_large_image',
    },
    geo,
    noIndex: input.noIndex !== undefined ? Boolean(input.noIndex) : !isIndexable(input),
  };
}

